'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Trash2, Upload } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { exportEntityCsv } from '@/lib/csv';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { GenericTag } from '@/styles/GenericTag';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { costCodeLabel, type CostCode } from '@/lib/cost-codes';
import { CostCodeForm } from './cost-code-form';
import { deleteCostCode, fetchCostCodes, fetchCostCodeUsage, reassignCostCodeReferences, type CostCodeUsage } from '../services/catalog-api';

const USES = [
  { key: 'catalog_items', label: 'items' },
  { key: 'purchase_orders', label: 'purchase orders' },
  { key: 'bills', label: 'bills' },
  { key: 'budget_lines', label: 'budget lines' },
] as const;

/** A table row: a cost code, its depth in the hierarchy and its place in the (sorted) hierarchy. */
interface CodeRow extends CostCode { depth: 0 | 1; childCount: number; order: number }

type SortKey = 'code' | 'name' | 'is_active';
const compare = (a: CostCode, b: CostCode, key: SortKey): number => {
  if (key === 'is_active') return Number(b.is_active) - Number(a.is_active) || a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });
  const left = key === 'name' ? (a.name ?? '') : a.code;
  const right = key === 'name' ? (b.name ?? '') : b.code;
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }) || a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * The company's cost codes: ONE entity, two levels. A cost code with no parent is a Parent (a group, shown as a header row with a chevron); a cost
 * code with a parent is a Child, nested under it. Search, sort, edit and delete work on every row. Selected rows (checkboxes) are deleted from the
 * menu; a parent that still has children is never deleted (the server and the database refuse it too).
 */
export function CostCodesTab() {
  const { companyId } = useUser();
  const [codes, setCodes] = useState<CostCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'code', direction: 'asc' });
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [editing, setEditing] = useState<CostCode | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const reload = useCallback(async () => {
    try {
      setCodes(await fetchCostCodes());
    } catch (failure) {
      console.error('Error loading the cost codes:', failure);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (companyId) void reload(); }, [companyId, reload]);

  // parents first (header rows), each followed by its children while expanded; a search keeps the parents of what matches open
  const rows = useMemo<CodeRow[]>(() => {
    const term = search.trim().toLowerCase();
    const matches = (c: CostCode) => !term || c.code.toLowerCase().includes(term) || (c.name ?? '').toLowerCase().includes(term) || (c.description ?? '').toLowerCase().includes(term);
    const direction = sort.direction === 'asc' ? 1 : -1;
    const ordered = [...codes].sort((a, b) => direction * compare(a, b, sort.key));
    const childrenOf = new Map<string, CostCode[]>();
    ordered.filter((c) => c.parent_id).forEach((c) => childrenOf.set(c.parent_id as string, [...(childrenOf.get(c.parent_id as string) ?? []), c]));
    const result: CodeRow[] = [];
    ordered.filter((c) => !c.parent_id).forEach((parent) => {
      const children = childrenOf.get(parent.id) ?? [];
      const parentMatches = matches(parent);
      const shownChildren = children.filter((c) => parentMatches || matches(c));
      if (!parentMatches && shownChildren.length === 0) return;
      result.push({ ...parent, depth: 0, childCount: children.length, order: result.length });
      if (term || !collapsed.includes(parent.id)) shownChildren.forEach((c) => result.push({ ...c, depth: 1, childCount: 0, order: result.length }));
    });
    return result;
  }, [codes, search, sort, collapsed]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: CostCode) => { setEditing(codes.find((c) => c.id === row.id) ?? row); setFormOpen(true); };
  const toggle = (id: string) => setCollapsed((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  // Same as every other table of the app: Delete in the menu acts on the ticked rows and asks with the browser's own confirmation.
  // The default child (0100) is the fallback of every item without a cost code, so it is never deleted; a parent is deleted only when none of its
  // children is left. Whatever uses a deleted code (items, purchase orders, bills, budget lines) is moved to the default code first.
  const handleDeleteSelected = async () => {
    if (selectedKeys.length === 0) {
      alert('Please select at least one cost code to delete.');
      return;
    }
    let all: CostCode[];
    let usages: CostCodeUsage[];
    try {
      all = await fetchCostCodes();
      const chosen = all.filter((c) => selectedKeys.includes(c.id) && !c.default_key);
      if (!all.some((c) => c.default_key) || chosen.length === 0) {
        alert('The default cost code (0100 · Default) cannot be deleted.');
        return;
      }
      const blocked = chosen.filter((c) => !c.parent_id && all.some((child) => child.parent_id === c.id && !chosen.some((x) => x.id === child.id)));
      if (blocked.length > 0) {
        alert(`${blocked.map((c) => c.code).join(', ')} ${blocked.length > 1 ? 'have' : 'has'} child cost codes. Move or delete the child cost codes first.`);
        return;
      }
      usages = await Promise.all(chosen.filter((c) => c.parent_id).map((c) => fetchCostCodeUsage(c.id)));
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Could not check where these cost codes are used');
      return;
    }
    const defaultCode = all.find((c) => c.default_key) as CostCode;
    const chosen = all.filter((c) => selectedKeys.includes(c.id) && !c.default_key);
    const used = usages.filter((usage) => usage.total > 0);
    const moved = USES.map(({ key, label }) => ({ label, count: usages.reduce((sum, usage) => sum + usage[key].count, 0) })).filter((t) => t.count > 0);
    const subject = chosen.length > 1 ? `these ${chosen.length} cost codes (${chosen.map((c) => c.code).join(', ')})` : `cost code ${chosen[0].code}`;
    const moves = used.length > 0 ? ` ${moved.map((t) => `${t.count} ${t.label}`).join(', ')} that use ${chosen.length === 1 ? 'it' : 'them'} will be moved to ${costCodeLabel(defaultCode)}.` : '';
    if (!window.confirm(`Delete ${subject}?${moves} This action cannot be undone.`)) return;

    const failed: string[] = [];
    try {
      for (const usage of used) await reassignCostCodeReferences(usage.cost_code.id, defaultCode.id);
      // children before their parent
      for (const c of [...chosen.filter((x) => x.parent_id), ...chosen.filter((x) => !x.parent_id)]) {
        try {
          await deleteCostCode(c.id);
        } catch (failure) {
          failed.push(`${c.code}: ${failure instanceof Error ? failure.message : 'failed'}`);
        }
      }
    } catch (failure) {
      alert(failure instanceof Error ? failure.message : 'Something went wrong');
    }
    if (failed.length > 0) alert(`Some cost codes could not be deleted. ${failed.join('; ')}`);
    setSelectedKeys([]);
    void reload();
  };

  const columns: GenericTableColumn<CodeRow>[] = [
    {
      key: 'code',
      header: 'Code',
      width: '14rem',
      sortable: true,
      sortValue: (row) => (sort.direction === 'asc' ? row.order : -row.order),
      render: (row) => (row.depth === 0 ? (
        <span className="flex items-center gap-1 font-semibold">
          <button
            type="button"
            className="p-0.5 -ml-1"
            aria-expanded={!collapsed.includes(row.id)}
            aria-label={`${collapsed.includes(row.id) ? 'Expand' : 'Collapse'} ${row.code}`}
            onClick={(event) => { event.stopPropagation(); toggle(row.id); }}
          >
            {collapsed.includes(row.id) ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {row.code}
        </span>
      ) : <span className="pl-8">{row.code}</span>),
    },
    { key: 'name', header: 'Name', width: '16rem', sortable: true, sortValue: (row) => (sort.direction === 'asc' ? row.order : -row.order), render: (row) => (row.depth === 0 ? <span className="font-semibold">{row.name || '-'}</span> : row.name || '-') },
    { key: 'type', header: 'Type', width: '9rem', render: (row) => (row.depth === 0 ? `Parent (${row.childCount})` : 'Child') },
    { key: 'description', header: 'Description', flexGrow: 2, render: (row) => row.description || '-' },
    {
      key: 'is_active',
      header: 'Status',
      width: '7rem',
      sortable: true,
      sortValue: (row) => (sort.direction === 'asc' ? row.order : -row.order),
      render: (row) => <GenericTag type="status" value={row.is_active ? 'active' : 'inactive'} />,
    },
  ];

  const menu = [
    // Import is not built yet: the entry exists, it does nothing for now
    { label: 'Import', icon: <Upload className="w-4 h-4" />, onClick: () => {} },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: () => exportEntityCsv(
        async (ids) => codes
          .filter((c) => !ids || ids.includes(c.id))
          .map((c) => ({ code: c.code, name: c.name ?? '', type: c.parent_id ? 'Child' : 'Parent', parent: codes.find((p) => p.id === c.parent_id)?.code ?? '', description: c.description ?? '', active: c.is_active })),
        selectedKeys,
        'cost-codes',
        'cost codes'
      ),
    },
    { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, destructive: true, onClick: () => void handleDeleteSelected() },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Cost Codes</h2>
        <div className="flex items-center gap-2">
          <NewItemButton label="New Cost Code" onClick={openNew} />
          <SearchButton onSearch={setSearch} placeholder="Search" />
          <ThreeDotMenuButton items={menu} />
        </div>
      </div>

      <GenericTable
        columns={columns}
        data={rows}
        loading={loading}
        pageSize={Math.max(rows.length, 1)}
        rowKey={(row) => row.id}
        minTableWidth={800}
        sort={{ columnKey: sort.key, direction: sort.direction, sortValue: (row: CodeRow) => (sort.direction === 'asc' ? row.order : -row.order) }}
        onSortChange={(next) => { if (next) setSort({ key: next.columnKey as SortKey, direction: next.direction }); }}
        onRowClick={openEdit}
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <CostCodeForm costCode={editing} codes={codes} isOpen={formOpen} onClose={() => setFormOpen(false)} onSaved={() => void reload()} />
    </div>
  );
}
